/**
 * RemindersPanel Component
 * Displays sustainability tips, reminders, and recommendations
 */

import React from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../ui/Icon';
import { PrefetchLink } from '../ui/PrefetchLink';
import { FEATURE_FLAGS } from '../../constants';
import { useComplianceReminders } from '../../hooks/useApi';
import type { ComplianceReminderFeedItem } from '../../types/api';
import { formatComplianceTypeLabel } from '../../features/compliance/lib/labels';
import { formatDateShort } from '../../utils/formatters';

interface RemindersPanelProps {
  className?: string;
}

const Panel = styled.div`
  min-width: 0;
  padding: 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
`;

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StateMessage = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-size: 0.75rem;
  color: ${(props) =>
    props.$error ? 'var(--danger-600)' : 'var(--text-muted)'};
`;

const ReminderCard = styled.div<{ priority?: 'high' | 'medium' | 'low' }>`
  background: var(--surface-strong);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid var(--border);
  position: relative;

  ${(props) => {
    switch (props.priority) {
      case 'high':
        return `
          border-left: 4px solid var(--danger-600);
          background: var(--surface-strong);
        `;
      case 'medium':
        return `
          border-left: 4px solid var(--warning-600);
          background: var(--surface-strong);
        `;
      case 'low':
      default:
        return `
          border-left: 4px solid var(--success-600);
          background: var(--surface-strong);
        `;
    }
  }}

  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: none;
  }
`;

const ReminderLink = styled(PrefetchLink)`
  display: block;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: none;
  }
`;

const ReminderHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ReminderIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1;
  flex-shrink: 0;
`;

const ReminderContent = styled.div`
  flex: 1;
`;

const ReminderTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ReminderText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const ReminderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  flex-wrap: wrap;
  color: var(--text-muted);
`;

const ReminderBadge = styled.span<{ type: 'tip' | 'goal' | 'alert' }>`
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.025em;

  ${(props) => {
    switch (props.type) {
      case 'alert':
        return `
          background: rgba(220, 53, 69, 0.1);
          color: var(--danger-600);
        `;
      case 'goal':
        return `
          background: rgba(199, 123, 26, 0.1);
          color: var(--warning-600);
        `;
      case 'tip':
      default:
        return `
          background: rgba(46, 125, 50, 0.1);
          color: var(--success-600);
        `;
    }
  }}
`;

type ReminderPriority = 'high' | 'medium' | 'low';
type ReminderBadgeType = 'tip' | 'goal' | 'alert';

interface ReminderItem {
  id: string;
  iconName: IconName;
  title: string;
  text: string;
  priority: ReminderPriority;
  type: ReminderBadgeType;
  meta?: string;
  link?: string;
  badgeLabel?: string;
}

const mapPriority = (value?: string): ReminderPriority => {
  if (value === 'high' || value === 'medium' || value === 'low') {
    return value;
  }
  return 'medium';
};

const mapBadgeType = (priority: ReminderPriority): ReminderBadgeType => {
  switch (priority) {
    case 'high':
      return 'alert';
    case 'low':
      return 'tip';
    default:
      return 'goal';
  }
};

/**
 * A document can be past its expiry date while its stored status is still
 * `active` (the backend only flips the status on its own schedule). Rely on the
 * expiry date so the badge reflects reality instead of the stale status flag.
 */
const isPastExpiry = (expiryDate?: string | null): boolean => {
  if (!expiryDate) {
    return false;
  }
  const [year, month, day] = expiryDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    return false;
  }
  const expiry = new Date(year, month - 1, day);
  if (Number.isNaN(expiry.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry.getTime() < today.getTime();
};

const isExpired = (item: ComplianceReminderFeedItem): boolean =>
  item.status === 'expired' || isPastExpiry(item.expiry_date);

const mapBadgeLabel = (item: ComplianceReminderFeedItem): string => {
  if (isExpired(item)) {
    return 'Vencido';
  }
  const priority = mapPriority(item.priority);
  return priority === 'low' ? 'Próximo' : 'Por vencer';
};

const mapIconName = (priority: ReminderPriority): IconName =>
  priority === 'high' ? 'warning' : 'calendar';

export const RemindersPanel: React.FC<RemindersPanelProps> = ({
  className,
}) => {
  const complianceEnabled = FEATURE_FLAGS.COMPLIANCE;
  const { data, isLoading, error } = useComplianceReminders({
    enabled: complianceEnabled,
  });

  const complianceReminders = React.useMemo<ReminderItem[]>(() => {
    if (!complianceEnabled || !data?.items) {
      return [];
    }

    return data.items.map((item) => {
      const priority = isExpired(item) ? 'high' : mapPriority(item.priority);
      return {
        id: item.document_id,
        iconName: mapIconName(priority),
        title:
          formatComplianceTypeLabel(item.type) || 'Documento de cumplimiento',
        text: item.message,
        priority,
        type: mapBadgeType(priority),
        ...(item.expiry_date && {
          meta: `Vence: ${formatDateShort(item.expiry_date)}`,
        }),
        link: item.link || `/compliance/${item.document_id}`,
        badgeLabel: mapBadgeLabel(item),
      };
    });
  }, [complianceEnabled, data?.items]);

  const getBadgeLabel = (reminder: ReminderItem) =>
    reminder.badgeLabel ||
    (reminder.type === 'tip'
      ? 'Consejo'
      : reminder.type === 'goal'
        ? 'Meta'
        : 'Alerta');

  return (
    <Panel className={className}>
      <Header>
        <Title>Recordatorios</Title>
        <Subtitle>Próximos vencimientos</Subtitle>
      </Header>

      <RemindersList>
        {complianceEnabled && isLoading && (
          <StateMessage role="status" aria-live="polite">
            Actualizando recordatorios de cumplimiento…
          </StateMessage>
        )}
        {complianceEnabled && error && (
          <StateMessage $error role="alert">
            No se pudieron cargar los recordatorios de cumplimiento.
          </StateMessage>
        )}
        {(!complianceEnabled || (!isLoading && !error)) &&
          complianceReminders.length === 0 && (
            <StateMessage>No hay recordatorios pendientes.</StateMessage>
          )}
        {complianceReminders.map((reminder) => {
          const card = (
            <ReminderCard priority={reminder.priority}>
              <ReminderHeader>
                <ReminderIcon role="img" aria-label={`${reminder.title} icon`}>
                  <Icon name={reminder.iconName} size="lg" />
                </ReminderIcon>
                <ReminderContent>
                  <ReminderTitle>{reminder.title}</ReminderTitle>
                  <ReminderText>{reminder.text}</ReminderText>
                  {(reminder.meta || reminder.badgeLabel) && (
                    <ReminderMeta>
                      <ReminderBadge type={reminder.type}>
                        {getBadgeLabel(reminder)}
                      </ReminderBadge>
                      {reminder.meta && (
                        <>
                          <span>•</span>
                          <span>{reminder.meta}</span>
                        </>
                      )}
                    </ReminderMeta>
                  )}
                </ReminderContent>
              </ReminderHeader>
            </ReminderCard>
          );

          if (reminder.link) {
            return (
              <ReminderLink
                key={reminder.id}
                to={reminder.link}
                aria-label={`Ver recordatorio de ${reminder.title}`}
              >
                {card}
              </ReminderLink>
            );
          }

          return <React.Fragment key={reminder.id}>{card}</React.Fragment>;
        })}
      </RemindersList>
    </Panel>
  );
};

export default RemindersPanel;
